import React, { useState } from 'react';
import { submitProtest } from '../utils/api';
import DefinitionPopover from './DefinitionPopover';

export default function ProtestModal({ cell, row, col, puzzle, onResolve, onCancel }) {
  const [argument, setArgument] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resolved, setResolved] = useState(false);
  const [resolveData, setResolveData] = useState(null);
  const [activeDefinition, setActiveDefinition] = useState(null);

  const toggleDefinition = (key) => {
    setActiveDefinition((prev) => (prev === key ? null : key));
  };

  const rowAdj = puzzle.rowAdjectives[row];
  const colAdj = puzzle.colAdjectives[col];

  const wordCount = argument.trim() ? argument.trim().split(/\s+/).length : 0;
  const overLimit = wordCount > 100;

  const handleSubmitProtest = async () => {
    if (!argument.trim() || loading || overLimit) return;

    const userMsg = argument.trim();
    setMessages((prev) => [...prev, { role: 'user', text: userMsg }]);
    setArgument('');
    setLoading(true);

    try {
      const result = await submitProtest(
        puzzle.date,
        row * 3 + col,
        cell.finalWord,
        rowAdj,
        colAdj,
        cell.finalScore,
        userMsg
      );

      setMessages((prev) => [
        ...prev,
        {
          role: 'judge',
          text: result.response || (result.accepted ? 'Protest accepted!' : 'Protest denied.'),
        },
      ]);

      // If the judge couldn't render a verdict, keep the modal in the input
      // state so the user can retry without burning their protest count.
      if (result.failed) {
        return;
      }

      setResolved(true);
      setResolveData({
        accepted: result.accepted,
        newScore: result.newScore,
        explanation: result.explanation,
      });
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          role: 'judge',
          text: 'Unable to reach the judge. Please try again.',
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = () => {
    if (resolveData) {
      onResolve(resolveData.accepted, resolveData.newScore, resolveData.explanation);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-cream border-2 border-ink shadow-xl max-w-md w-full max-h-[80vh] flex flex-col">
        <div className="p-4 border-b-2 border-ink">
          <h3 className="font-serif font-bold text-lg text-ink">Protest Square</h3>
          <div className="text-sm text-ink-light font-body">
            <span className="relative inline-block">
              <span className="font-semibold text-ink">{rowAdj}</span>
              <button
                onClick={() => toggleDefinition('row')}
                className="text-ink-faint hover:text-ink text-[10px] cursor-pointer ml-0.5"
                aria-label={`Definition of ${rowAdj}`}
              >
                &#9432;
              </button>
              {activeDefinition === 'row' && (
                <DefinitionPopover
                  word={rowAdj}
                  definition={puzzle.definitions?.[rowAdj]}
                  onClose={() => setActiveDefinition(null)}
                  position="below"
                />
              )}
            </span>
            {' + '}
            <span className="relative inline-block">
              <span className="font-semibold text-ink">{colAdj}</span>
              <button
                onClick={() => toggleDefinition('col')}
                className="text-ink-faint hover:text-ink text-[10px] cursor-pointer ml-0.5"
                aria-label={`Definition of ${colAdj}`}
              >
                &#9432;
              </button>
              {activeDefinition === 'col' && (
                <DefinitionPopover
                  word={colAdj}
                  definition={puzzle.definitions?.[colAdj]}
                  onClose={() => setActiveDefinition(null)}
                  position="below"
                />
              )}
            </span>
          </div>
          <div className="mt-2 flex items-center gap-2">
            <span className="font-serif font-bold text-ink">{cell.finalWord}</span>
            <span className="px-2 py-0.5 text-xs font-bold bg-parchment border border-ink-faint">
              {cell.finalScore.toFixed(1)}
            </span>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[200px]">
          {messages.length === 0 && (
            <p className="text-sm text-ink-faint text-center mt-8 font-body italic">
              Explain why your answer deserves a higher score...
            </p>
          )}
          {messages.map((msg, i) => (
            <div
              key={i}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] px-3 py-2 text-sm font-body ${
                  msg.role === 'user'
                    ? 'bg-ink text-cream'
                    : 'bg-parchment text-ink border border-ink-faint'
                }`}
              >
                {msg.text}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-parchment px-3 py-2 border border-ink-faint">
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-ink-faint rounded-full animate-bounce" />
                  <span className="w-2 h-2 bg-ink-faint rounded-full animate-bounce [animation-delay:0.1s]" />
                  <span className="w-2 h-2 bg-ink-faint rounded-full animate-bounce [animation-delay:0.2s]" />
                </div>
              </div>
            </div>
          )}
        </div>

        {resolved ? (
          <div className="p-4 border-t-2 border-ink">
            {resolveData && (() => {
              const change = resolveData.newScore - cell.finalScore;
              return (
                <div className="mb-3 p-3 border-2 border-ink text-center">
                  <p className="text-[10px] font-body text-ink-faint uppercase tracking-widest mb-1">Judgement</p>
                  <p className={`text-xl font-serif font-bold ${
                    change > 0 ? 'text-green-700' : change < 0 ? 'text-red-700' : 'text-ink'
                  }`}>
                    {change > 0 ? `+${change.toFixed(1)}` : change < 0 ? change.toFixed(1) : 'No change'}
                  </p>
                  <p className="text-xs font-body text-ink-light mt-1">
                    {cell.finalScore.toFixed(1)} {'\u2192'} {resolveData.newScore.toFixed(1)}
                  </p>
                </div>
              );
            })()}
            <button
              onClick={handleContinue}
              className="w-full py-3 bg-ink text-cream font-serif font-bold border-2 border-ink hover:bg-ink-light transition-colors"
            >
              Continue
            </button>
          </div>
        ) : (
          <div className="p-4 border-t-2 border-ink">
            <div className="flex gap-2">
              <textarea
                value={argument}
                onChange={(e) => setArgument(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmitProtest();
                  }
                }}
                placeholder="Make your case (100 words max)..."
                className="flex-1 px-3 py-2 border-2 border-ink text-sm font-body focus:outline-none focus:border-ink-light bg-white resize-none"
                disabled={loading}
                rows={3}
              />
              <button
                onClick={handleSubmitProtest}
                disabled={loading || !argument.trim() || overLimit}
                className="px-4 py-2 bg-ink text-cream text-sm font-serif font-semibold border-2 border-ink hover:bg-ink-light disabled:opacity-50 transition-colors self-end"
              >
                Send
              </button>
            </div>
            {wordCount >= 90 && (
              <p className={`text-xs mt-1 font-body ${overLimit ? 'text-red-600 font-bold' : 'text-ink-faint'}`}>
                {wordCount}/100
              </p>
            )}
            <button
              onClick={onCancel}
              className="w-full mt-2 py-2 text-sm text-ink-faint hover:text-ink font-body transition-colors"
            >
              Cancel protest
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
